import type { GovernancePolicyFormData } from '@experiences/interfaces';
import {
    useCallback,
    useMemo,
} from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
    useStepHooks,
    useUiStepper,
} from '../../../../common/UiWizard';

interface UseGovernancePolicyCreateReviewAndSaveStepViewModelProps {
    type?: 'add' | 'edit';
    mode?: 'create' | 'edit';
}

const useGovernancePolicyCreateReviewAndSaveStepViewModel = (
    props: UseGovernancePolicyCreateReviewAndSaveStepViewModelProps = {},
) => {
    const { mode = 'create' } = props;
    const { formatMessage: translate } = useIntl();
    const {
        handleSubmit, formState: { isDirty },
    } = useFormContext<GovernancePolicyFormData>();
    const {
        isLastStep,
        onSubmitHandler,
        goToStep,
        setNextLoading,
    } = useUiStepper();

    // The parent owns the submission logic (API calls, navigation, etc.).
    // The step component only validates and triggers the submission.
    const handleStepComplete = useCallback(async () => {
        if (!isLastStep || !onSubmitHandler) {
            return;
        }

        setNextLoading(true);

        try {
            await handleSubmit(async (data) => {
                await onSubmitHandler(data as GovernancePolicyFormData);
            })();
        } finally {
            setNextLoading(false);
        }
    }, [ isLastStep, onSubmitHandler, handleSubmit, setNextLoading ]);

    const stepHooks = useMemo(() => ({
        onComplete: handleStepComplete,
        nextLabel: translate({ id: 'CLIENT_SAVE' }),
        // In edit mode, disable Save button if form hasn't changed
        disableNext: mode === 'edit' ? !isDirty : false,
    }), [ handleStepComplete, translate, mode, isDirty ]);

    useStepHooks(stepHooks);

    // Edit handlers: goToStep for wizard steps (0: Scope, 1: Policy Definition, 2: Details, 3: Enforcement, 4: Review and Save)
    const handleEditPolicyDefinition = useCallback(() => goToStep(1), [ goToStep ]);
    const handleEditNameAndDescription = useCallback(() => goToStep(2), [ goToStep ]);
    const handleEditEnforcement = useCallback(() => goToStep(3), [ goToStep ]);

    return {
        handleEditPolicyDefinition,
        handleEditNameAndDescription,
        handleEditEnforcement,
    };
};

export default useGovernancePolicyCreateReviewAndSaveStepViewModel;

